import React from "react";
import { dayjs } from "Config/dayjs";
import { Modal } from "Components/General/Modal/GenericModal";
import { I18N_NS } from "Config/i18n";
import { Divider, Input, Row, Col, Select, Steps } from "antd";
import { Button } from "Components/General/Button";
import Text from "antd/lib/typography/Text";
import { useTranslation } from "react-i18next";
import { ValidValuesPicker } from "./MultiValuesPickerModal";
import { ConventionChecker } from "./ConventionChecker";
import { hasBlacklistedFilenameChars, getBlacklistedFilenameCharsMessage } from "utils/inputValidation";

export interface ConventionData {
  value: string;
  length?: number;
  exactLength: boolean;
  regexp: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validValues?: any[];
  name?: string;
  description?: string;
  additonalCharFilter?: string[];
  format?: API_FILENAMEASSISTANTCONVENTIONRESPONSE_FORMAT;
  dateFormat?: string;
}

export interface ModalData extends ConventionData {
  conventions?: ConventionData[];
}

interface CommonProps {
  isVisible: boolean;
  title: string;
  onCancel: () => void;
  onChange: (val: string) => void;
  multiConventionCase?: "AND" | "OR";
  data: ModalData[];
  initialValue: string;
}

interface FreeTextInputProps extends Omit<CommonProps, "visible" | "title" | "isVisible" | "initialValue"> {
  hideButtons?: boolean;
  onLocalChange?: (value: string) => void;
  onValidityChange?: (isValid: boolean) => void;
}

interface MultiConventionInputProps extends Omit<CommonProps, "title" | "visible" | "isVisible"> {}

//
export const TextValueInput: React.FC<FreeTextInputProps> = ({
  data,
  onCancel,
  onChange,
  multiConventionCase,
  hideButtons,
  onLocalChange,
  onValidityChange,
}) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [inputValue, setValue] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const activeData = data[0];
  const isInitializing = React.useRef<boolean>(false);

  const length = activeData.length === -1 ? 1 : activeData.length;

  let descriptionMessage: string = "";

  if (activeData.description) {
    descriptionMessage += activeData.description;
  } else if (activeData.dateFormat) {
    descriptionMessage += t(`Please, provide a date in the format {{format}}`, { format: activeData.dateFormat });
  } else if (activeData.regexp) {
    descriptionMessage += t(`Please, provide the value that matches the expression {{reg}}`, {
      reg: activeData.regexp,
    });
  } else if (activeData.exactLength) {
    descriptionMessage += t(`Please, provide the value of {{length}} characters length`, { length });
  } else {
    descriptionMessage += t(`Please, provide the value of at least {{length}} characters`, { length });
  }

  const completeDescriptionMessage = activeData.name
    ? `${activeData.name} (${descriptionMessage})`
    : descriptionMessage;

  const isValidValue = React.useCallback(() => {
    const { length: inputValidLength, exactLength, regexp, dateFormat } = activeData;
    if (inputValue) {
      if (dateFormat) {
        dayjs(inputValue, dateFormat.toUpperCase(), true).isValid()
          ? checkCharsAllowed() && setErrorMessage(undefined)
          : setErrorMessage(t(`Please, provide a valid date in the format {{format}}`, { format: dateFormat }));
      } else if (regexp) {
        inputValue.search(new RegExp(`^${regexp}$`)) === -1
          ? setErrorMessage(t(`The value must match the {{reg}} expression`, { reg: activeData.regexp }))
          : setErrorMessage(undefined);
      } else {
        if (exactLength) {
          if (inputValue.length === inputValidLength) {
            checkCharsAllowed() && setErrorMessage(undefined);
          } else {
            setErrorMessage(t(`The value must be {{length}} characters`, { length }));
          }
        } else {
          if (inputValue.length >= inputValidLength) {
            checkCharsAllowed() && setErrorMessage(undefined);
          } else {
            setErrorMessage(t(`The value must be at least {{length}} characters`, { length }));
          }
        }
      }
    } else {
      setErrorMessage("");
    }

    function checkCharsAllowed() {
      if (hasBlacklistedFilenameChars(inputValue)) {
        setErrorMessage(getBlacklistedFilenameCharsMessage(t));
        return false;
      }
      if (
        activeData.additonalCharFilter.length > 0 &&
        activeData.additonalCharFilter.find((char) => inputValue.indexOf(char) > -1)
      ) {
        setErrorMessage(
          t(`It's not allowed to use this '{{filter}}' character(s)`, {
            filter: activeData.additonalCharFilter,
          })
        );
        return false;
      }
      return true;
    }
  }, [inputValue, activeData]);

  const handleSave = () => {
    if (!errorMessage) {
      if (onLocalChange) {
        onLocalChange(inputValue);
      } else {
        onChange(inputValue);
      }
    }
  };

  const handleUserInput = (value: string) => {
    isInitializing.current = false;
    setValue(value);
  };

  React.useEffect(() => {
    isValidValue();
  }, [inputValue, isValidValue]);

  React.useEffect(() => {
    if (onLocalChange && !isInitializing.current) {
      onLocalChange(inputValue);
    }
  }, [inputValue]);

  React.useEffect(() => {
    if (onValidityChange) {
      const isValid = !errorMessage && inputValue !== "";
      onValidityChange(isValid);
    }
  }, [errorMessage, inputValue]);

  if (!data || !data.length) {
    return null;
  }

  return (
    <>
      <div className="mb-2">
        <div className="mb-1">
          <span style={{ color: "#ff0000", marginRight: 4 }}>*</span>
          {completeDescriptionMessage}
        </div>
        <Input
          value={inputValue}
          onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => handleUserInput(value)}
          className={errorMessage && "input-error"}
          autoFocus={true}
        />
        <Text className={!errorMessage && "opacity-0"} type="danger">
          {errorMessage}
        </Text>
      </div>
      {!hideButtons && (
        <Row justify="space-between">
          <Button onClick={onCancel}>{multiConventionCase ? t("Back") : t("Cancel")}</Button>
          <Button type="primary" onClick={handleSave} disabled={!!errorMessage}>
            {t("Save")}
          </Button>
        </Row>
      )}
    </>
  );
};

//
export const MultiConventionInput: React.FC<MultiConventionInputProps> = ({
  data,
  initialValue,
  multiConventionCase,
  onCancel,
  onChange,
}) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [inputValue, setValue] = React.useState<string[]>(Array.from(data, () => ""));
  const [currentStep, setCurrentStep] = React.useState<number>(0);
  const [activeInfo, setActiveInfo] = React.useState<{
    ["name"]: string;
    ["indexId"]: number;
  }>({
    name: "Convention",
    indexId: -1,
  });
  const [activeConvention, setActiveConvention] = React.useState<ModalData>(null);
  const [isValid, setValid] = React.useState<boolean[]>(Array.from(data, () => false));
  const [nestedValues, setNestedValues] = React.useState<string[]>([]);
  const [nestedInitialValues, setNestedInitialValues] = React.useState<string[]>([]);
  const [nestedValidity, setNestedValidity] = React.useState<boolean[]>([]);

  const errorMessage = (data: ModalData, value?: string) => {
    if (value && hasBlacklistedFilenameChars(value)) {
      return getBlacklistedFilenameCharsMessage(t);
    }
    if (data?.dateFormat) {
      return t(`Please, provide a valid date in the format {{format}}`, { format: data.dateFormat });
    }
    if (data?.regexp) {
      return t(`The value must match the {{reg}} expression`, { reg: data.regexp });
    }
    if (data?.exactLength) {
      return t(`The value must be {{length}} characters`, { length: data.length });
    }
    return t(`The value must be at least {{length}} characters`, { length: data.length });
  };

  const descriptionMessage = (data: ModalData): string => {
    let message: string;
    if (data?.dateFormat) {
      message = data.description || t(`Please, provide a date in the format {{format}}`, { format: data.dateFormat });
    } else if (data?.validValues?.length > 0 && !data?.regexp) {
      message = t(`please choose a value`);
    } else if (data?.regexp) {
      if (data.description) {
        message = data.description;
      } else {
        message = t(`Please, provide the value that matches the expression {{reg}}`, { reg: data.regexp });
      }
    } else if (data?.exactLength) {
      message = t(`Please, provide the value of {{length}} characters length`, { length: data.length });
    } else {
      message = t(`Please, provide the value of at least {{length}} characters`, { length: data.length });
    }
    return message;
  };

  const parseInitialValues = (data: ModalData[]) => {
    let startIdx: number = 0;
    return data.map((v) => {
      const ret = v.length === -1 ? v.value.slice(startIdx) : v.value.slice(startIdx, startIdx + v.length);
      startIdx += v.length;
      return ret;
    });
  };

  const parseNestedConventionValues = (parentValue: string, conventions: ConventionData[]) => {
    let startIdx: number = 0;
    return conventions.map((conv) => {
      const ret =
        conv.length === -1 ? parentValue.slice(startIdx) : parentValue.slice(startIdx, startIdx + conv.length);
      startIdx += conv.length === -1 ? ret.length : conv.length;
      return ret;
    });
  };

  const isValidValue = React.useCallback(() => {
    const validState: boolean[] = [];
    ConventionChecker.check(inputValue.join(""), data, validState);

    inputValue.forEach((val, idx) => {
      if (val && hasBlacklistedFilenameChars(val)) {
        validState[idx] = false;
      }
    });

    setValid(validState);
  }, [inputValue, data]);

  const handleSave = () => {
    if (isValid) {
      onChange(inputValue.join(""));
    }
  };

  const handleCancel = () => {
    setCurrentStep(0);
    setActiveInfo({ name: "Convention", indexId: -1 });
    setActiveConvention(null);
    setNestedValues([]);
    setNestedInitialValues([]);
    setNestedValidity([]);
  };

  const handleSaveNestedConventionsValue = () => {
    const combinedValue = nestedValues.join("");
    onChange(combinedValue);
  };

  const handleNestedValueChange = React.useCallback((index: number, value: string) => {
    setNestedValues((prev) => {
      const newValues = [...prev];
      newValues[index] = value;
      return newValues;
    });
  }, []);

  const handleNestedValidityChange = React.useCallback((index: number, isValid: boolean) => {
    setNestedValidity((prev) => {
      const newValidity = [...prev];
      newValidity[index] = isValid;
      return newValidity;
    });
  }, []);

  const renderConventions = () => {
    const isNestedConventions = activeConvention.conventions?.length > 1;

    return isNestedConventions ? (
      <>
        {activeConvention.conventions.map((convention, index) => (
          <React.Fragment key={index}>
            {renderConventionPicker(convention, index, true)}
            <Divider />
          </React.Fragment>
        ))}
        <Row justify="space-between" className="mt-md">
          <Button onClick={handleCancel}>{multiConventionCase ? t("Back") : t("Cancel")}</Button>
          <Button
            type="primary"
            onClick={handleSaveNestedConventionsValue}
            disabled={
              nestedValues.length !== activeConvention.conventions.length ||
              nestedValues.some((v) => !v) ||
              nestedValidity.some((v) => !v)
            }
          >
            {t("Save")}
          </Button>
        </Row>
      </>
    ) : (
      renderConventionPicker(activeConvention, 0, false)
    );
  };

  const renderConventionPicker = (convention: ConventionData, index: number, hideButtons: boolean) => {
    const initialValueForAlert = hideButtons ? nestedInitialValues[index] || "" : convention.value || "";

    const localChangeHandler = hideButtons ? (value: string) => handleNestedValueChange(index, value) : undefined;

    const validityChangeHandler = hideButtons
      ? (isValid: boolean) => handleNestedValidityChange(index, isValid)
      : undefined;

    const isMultiValuesPicker =
      convention.validValues !== null && convention.regexp === null && convention.format !== "DATUM";
    return isMultiValuesPicker ? (
      <ValidValuesPicker
        multiConventionCase={multiConventionCase}
        onCancel={handleCancel}
        onChange={onChange}
        data={{
          values: convention.validValues as { name: string; description: string }[],
          initialValue: initialValueForAlert,
          activePartConventions: [convention],
        }}
        hideButtons={hideButtons}
        onLocalChange={localChangeHandler}
        onValidityChange={validityChangeHandler}
      />
    ) : (
      <TextValueInput
        multiConventionCase={multiConventionCase}
        onCancel={handleCancel}
        onChange={onChange}
        data={[{ ...convention, value: initialValueForAlert }]}
        hideButtons={hideButtons}
        onLocalChange={localChangeHandler}
        onValidityChange={validityChangeHandler}
      />
    );
  };

  const renderCaseOR = () => {
    return (
      <>
        <Steps
          size="small"
          current={currentStep}
          className="mb-2"
          items={[{ title: t("Overview") }, { title: t(activeInfo.name) }]}
        />
        <div className="mt-2">
          {currentStep === 0
            ? data.map((item, index) => (
                <div
                  key={`${item.name}-${index}`}
                  className="mb-2 pointer"
                  onClick={() => {
                    setActiveConvention(data[index]);
                    setActiveInfo({ name: item.name, indexId: index });
                    setCurrentStep(1);
                  }}
                >
                  <Text className="mr-10" underline strong>
                    {item.name}
                  </Text>
                  {item.description && <Text>({item.description})</Text>}
                </div>
              ))
            : renderConventions()}
        </div>
      </>
    );
  };

  const renderCaseAND = () => {
    return (
      <>
        <div className="mb-2" style={{ overflow: "auto", maxHeight: "calc(100% - 40px)" }}>
          <Row justify="end" align="middle">
            {data.map((convention, idx) => (
              <React.Fragment key={idx}>
                <Col flex="1">
                  <div className="mb-1">{descriptionMessage(convention)}</div>
                  {!convention?.regexp && !convention?.dateFormat && convention?.validValues?.length ? (
                    <Select
                      value={inputValue[idx]}
                      onChange={(newValue) => {
                        const newInputValue = [...inputValue];
                        newInputValue[idx] = newValue;
                        setValue(newInputValue);
                        isValidValue();
                      }}
                      className={!isValid[idx] && "input-error"}
                      style={{ width: "100%" }}
                    >
                      {convention.validValues.map((item) => (
                        <Select.Option key={item.name} value={item.name}>
                          {item.name} {item.description && [item.description]}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      value={inputValue[idx]}
                      onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                        const newInputValue = [...inputValue];
                        newInputValue[idx] = value;
                        setValue(newInputValue);
                        isValidValue();
                      }}
                      className={!isValid[idx] && "input-error"}
                    />
                  )}
                  <Text className={isValid[idx] && "opacity-0"} type="danger">
                    {errorMessage(data[idx], inputValue[idx])}
                  </Text>
                </Col>
                {idx !== data.length - 1 ? <Divider type="horizontal">{"AND"}</Divider> : ""}
              </React.Fragment>
            ))}
          </Row>
        </div>
        <Row justify="space-between">
          <Button onClick={onCancel}>{t("Cancel")}</Button>
          <Button type="primary" onClick={handleSave} disabled={isValid.includes(Boolean(0))}>
            {t("Save")}
          </Button>
        </Row>
      </>
    );
  };

  React.useEffect(() => {
    const values = parseInitialValues(data);
    setValue(values);
  }, [data]);

  React.useEffect(() => {
    isValidValue();
  }, [inputValue, isValidValue]);

  React.useEffect(() => {
    if (activeConvention?.conventions && activeConvention.conventions.length > 1) {
      const emptyValues = activeConvention.conventions.map(() => "");
      setNestedValues(emptyValues);

      const parsedValues = parseNestedConventionValues(activeConvention.value || "", activeConvention.conventions);
      setNestedInitialValues(parsedValues);

      const initialValidity = activeConvention.conventions.map(() => false);
      setNestedValidity(initialValidity);
    }
  }, [activeConvention]);

  return (
    <>
      <div className="mb-lg">{multiConventionCase === "OR" ? renderCaseOR() : renderCaseAND()}</div>
      {currentStep === 0 && multiConventionCase === "OR" && (
        <Row justify="start">
          <Button onClick={onCancel}>{t("Cancel")}</Button>
        </Row>
      )}
    </>
  );
};

//
export const CellValueEditorModal: React.FC<CommonProps> = ({
  isVisible,
  title,
  data,
  initialValue,
  multiConventionCase,
  onCancel,
  onChange,
}) => {
  const renderContent = (data: ConventionData[]) => {
    if (!multiConventionCase) {
      return <TextValueInput onCancel={onCancel} onChange={onChange} data={data} />;
    }

    return (
      <MultiConventionInput
        multiConventionCase={multiConventionCase}
        onCancel={onCancel}
        onChange={onChange}
        initialValue={initialValue}
        data={data}
      />
    );
  };

  if (!data || !data.length) {
    return null;
  }

  return (
    <Modal
      destroyOnHidden={true}
      maskClosable={false}
      className={multiConventionCase !== "AND" ? undefined : "full-height-modal"}
      title={title}
      open={isVisible}
      width={"100%"}
      style={{ maxWidth: 720, top: 40 }}
      zIndex={1012}
      onCancel={onCancel}
      closable={false}
      footer={null}
    >
      {renderContent(data)}
    </Modal>
  );
};
